// ============================================================================
// Integration Provider Card — Summary View
// ============================================================================
// Clickable summary card for the overview page. Navigates to detail page.

import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { ChevronRight } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { OverallHealthBadge } from './OverallHealthBadge'
import { ProviderIcon } from './ProviderIcon'
import type { IntegrationOverview } from '@/types/integrations'

interface IntegrationProviderCardProps {
  overview: IntegrationOverview
}

export function IntegrationProviderCard({ overview }: IntegrationProviderCardProps) {
  const navigate = useNavigate()

  const provider = overview.integration_providers
  const providerName = provider?.name ?? overview.provider
  const providerType = provider?.provider_type ?? 'reservation'
  const credentials = overview.integration_credentials

  const activeCount = credentials.filter(c => c.is_active).length

  // Find most recent sync across all credentials
  const lastSyncTimes = credentials
    .map(c => c.last_successful_sync)
    .filter(Boolean) as string[]
  const mostRecentSync = lastSyncTimes.length > 0
    ? new Date(Math.max(...lastSyncTimes.map(t => new Date(t).getTime())))
    : null

  // Aggregate errors
  const totalFailures = credentials.reduce((sum, c) => sum + c.consecutive_failures, 0)

  return (
    <Card
      className={`cursor-pointer transition-colors hover:bg-muted/50 ${!overview.is_active ? 'opacity-60' : ''}`}
      onClick={() => navigate(`/settings/integrations/${overview.id}`)}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <ProviderIcon providerType={providerType} />
          <div>
            <CardTitle className="text-base font-semibold">{providerName}</CardTitle>
            <p className="text-xs text-muted-foreground">{overview.label}</p>
          </div>
        </div>
        <OverallHealthBadge credentials={credentials} />
      </CardHeader>

      <CardContent>
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            <span>{activeCount} location{activeCount !== 1 ? 's' : ''} active</span>
            <span className="text-border">|</span>
            <span>
              {mostRecentSync
                ? `Last sync: ${formatDistanceToNow(mostRecentSync, { addSuffix: true })}`
                : 'Never synced'}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {totalFailures > 0 && (
              <span className="text-destructive text-xs">
                {totalFailures} error{totalFailures !== 1 ? 's' : ''}
              </span>
            )}
            <ChevronRight className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
